import React, { useState, useEffect, useCallback } from 'react';
import { Download, Upload, Shield, Clock, CheckCircle, XCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getApiUrl } from '@/config';
import { DataTools } from './DataTools';
import { SecurityLogsView } from './SecurityLogsView';
import { isDemoDataDisabled, setDemoDataDisabled } from '@shared/utils/demoDataControl';

interface LogEntry {
    id: string;
    timestamp: string;
    username: string;
    status: 'success' | 'failure';
}

const ConnectionStatus: React.FC = () => {
    const [dbStatus, setDbStatus] = useState<'checking' | 'ok' | 'error'>('checking');
    const [apiStatus, setApiStatus] = useState<'checking' | 'ok' | 'error'>('checking');
    const [uiStatus] = useState<'checking' | 'ok' | 'error'>('ok'); // Always OK if app loads

    const checkHealth = useCallback(async () => {
        setDbStatus('checking');
        setApiStatus('checking');
        try {
            // Check API Reachability
            const token = localStorage.getItem('token');
            const headers: Record<string, string> = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(getApiUrl('/api/technicians'), { headers }); // Lightweight fetch

            if (res.ok) {
                setApiStatus('ok');
                // Check DB (If fetch returned data, DB is reachable)
                try {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        setDbStatus('ok');
                    } else {
                        setDbStatus('error');
                    }
                } catch {
                    setDbStatus('error');
                }
            } else {
                setApiStatus('error');
                setDbStatus('error');
            }
        } catch (e) {
            setApiStatus('error');
            setDbStatus('error');
        }
    }, []);

    useEffect(() => {
        checkHealth();
    }, [checkHealth]);

    const StatusRow = ({ label, status, subtext }: { label: string, status: 'checking' | 'ok' | 'error', subtext?: string }) => (
        <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg border border-white/5">
            <div>
                <span className="text-gray-300 block">{label}</span>
                {subtext && <span className="text-xs text-gray-500">{subtext}</span>}
            </div>
            <div className="flex items-center gap-2">
                {status === 'checking' && (
                    <span className="text-yellow-400 text-sm font-medium flex items-center gap-1">
                        <Clock size={14} className="animate-spin" /> Vérification...
                    </span>
                )}
                {status === 'ok' && (
                    <span className="text-green-400 text-sm font-medium flex items-center gap-1">
                        <CheckCircle size={14} /> OK
                    </span>
                )}
                {status === 'error' && (
                    <span className="text-red-400 text-sm font-medium flex items-center gap-1">
                        <XCircle size={14} /> Erreur
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="space-y-3">
            <StatusRow label="Interface Utilisateur" status={uiStatus} subtext="Client Frontend" />
            <StatusRow label="Serveur API" status={apiStatus} subtext="Backend Node.js" />
            <StatusRow label="Base de Données" status={dbStatus} subtext="Persistence MongoDB" />

            <button
                onClick={checkHealth}
                className="w-full mt-4 py-2 text-xs text-center text-gray-500 hover:text-white hover:bg-white/5 rounded transition-colors"
            >
                Rafraîchir les statuts
            </button>
        </div>
    );
};

export const AdminPanel: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [restoreStatus, setRestoreStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [restoreMessage, setRestoreMessage] = useState('');

    useEffect(() => {
        fetchLogs();
        const interval = setInterval(fetchLogs, 30000); // Auto refresh logs
        return () => clearInterval(interval);
    }, []);

    const fetchLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/logs'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                // The server now returns { logs: [], stats: {} }, so we need data.logs
                setLogs(Array.isArray(data.logs) ? data.logs : []);
                return;
            }
        } catch (e) {
            console.error('Failed to fetch logs from API, using localStorage', e);
        }

        // Fallback: Read logs from localStorage
        try {
            const localLogs = JSON.parse(localStorage.getItem('loginLogs') || '[]');
            // Use spread to avoid modifying original array
            setLogs([...localLogs].reverse()); // Show most recent first
        } catch (e) {
            console.error('Failed to read logs from localStorage', e);
            setLogs([]);
        }
    };

    const handleRestore = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!confirm('⚠️ ATTENTION: Cette action va ÉCRASER toutes les données actuelles. Êtes-vous sûr de vouloir continuer ?')) {
            event.target.value = '';
            return;
        }

        setIsLoading(true);
        setRestoreStatus('idle');
        setRestoreMessage('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/restore'), {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await res.json();

            if (res.ok) {
                setRestoreStatus('success');
                setRestoreMessage('Restauration réussie ! La page va se rafraîchir...');
                setTimeout(() => window.location.reload(), 2000);
            } else {
                setRestoreStatus('error');
                setRestoreMessage(data.error || 'Erreur lors de la restauration');
            }
        } catch (e: any) {
            setRestoreStatus('error');
            setRestoreMessage(e.message || 'Erreur lors de la restauration');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClientBackup = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/backup'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!res.ok) {
                throw new Error('Erreur lors de la récupération de la sauvegarde');
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `backup-vigie-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (e) {
            console.error('Backup failed', e);
            alert('Erreur lors de la création de la sauvegarde.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-100 mb-6 flex items-center gap-3">
                <Shield className="text-primary" size={32} />
                Administration Système
            </h1>

            {/* Data Tools Section - Backup, Restore, Clear All */}
            <DataTools />

            {/* Security Logs Section */}
            <SecurityLogsView />

            {/* System Configuration */}
            <div className="glass-panel p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-200 mb-4 flex items-center gap-2">
                    <Shield size={20} className="text-blue-400" />
                    Configuration Système
                </h2>
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/5">
                    <div>
                        <h3 className="text-gray-200 font-medium">Mode Production Strict</h3>
                        <p className="text-sm text-gray-400">
                            Empêcher le chargement automatique des données de démonstration si le serveur est inaccessible.
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            defaultChecked={isDemoDataDisabled()}
                            onChange={(e) => {
                                setDemoDataDisabled(e.target.checked);
                                alert('Paramètre enregistré. Veuillez recharger la page pour appliquer les changements.');
                            }}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Backup / Restore Section */}
                <div className="glass-panel p-6 space-y-4">
                    <h2 className="text-xl font-semibold text-gray-200 flex items-center gap-2">
                        <Download size={20} className="text-accent" />
                        Sauvegarde et Restauration
                    </h2>
                    <p className="text-sm text-gray-400">
                        Gérez les copies de sécurité de vos données (Techniciens, Chantiers, Utilisateurs).
                    </p>

                    <div className="flex flex-col gap-3 pt-4">
                        <button
                            onClick={handleClientBackup}
                            disabled={isLoading}
                            className="glass-button flex items-center justify-center gap-2 py-3 bg-accent/20 hover:bg-accent/30 text-accent border-accent/50 w-full"
                        >
                            <Download size={18} />
                            {isLoading ? 'Génération...' : 'Télécharger une sauvegarde complète (.json)'}
                        </button>

                        <div className="relative">
                            <input
                                type="file"
                                accept=".json"
                                onChange={handleRestore}
                                className="hidden"
                                id="restore-file"
                                disabled={isLoading}
                            />
                            <label
                                htmlFor="restore-file"
                                className={`glass-button flex items-center justify-center gap-2 py-3 w-full cursor-pointer bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <Upload size={18} />
                                {isLoading ? 'Restauration en cours...' : 'Restaurer une sauvegarde (.json)'}
                            </label>
                        </div>

                        {restoreStatus !== 'idle' && (
                            <div className={`p-3 rounded-lg text-sm border ${restoreStatus === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
                                {restoreMessage}
                            </div>
                        )}

                        <div className="text-xs text-gray-500 mt-2">
                            Note: Le fichier téléchargé peut être utilisé directement pour la restauration.
                        </div>
                    </div>
                </div>

                {/* Server Status */}
                <div className="glass-panel p-6">
                    <h2 className="text-xl font-semibold text-gray-200 mb-4">État des Services</h2>
                    <ConnectionStatus />
                </div>
            </div>

            {/* Connection Logs */}
            <div className="glass-panel p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-200 flex items-center gap-2">
                        <Clock size={20} className="text-gray-400" />
                        Journal des Connexions
                    </h2>
                    <button onClick={fetchLogs} className="text-xs text-accent hover:underline">
                        Actualiser
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 text-gray-400 text-sm">
                                <th className="py-2 pl-2">Date & Heure</th>
                                <th className="py-2">Utilisateur</th>
                                <th className="py-2 pr-2 text-right">Statut</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="py-8 text-center text-gray-500 italic">
                                        Aucun journal disponible
                                    </td>
                                </tr>
                            ) : (
                                logs.map(log => (
                                    <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="py-2 pl-2 text-gray-300 font-mono text-xs">
                                            {format(parseISO(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                                        </td>
                                        <td className="py-2 text-gray-200 font-medium">
                                            {log.username}
                                        </td>
                                        <td className="py-2 pr-2 text-right">
                                            {log.status === 'success' ? (
                                                <span className="inline-flex items-center gap-1 text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full text-xs">
                                                    <CheckCircle size={10} /> Succès
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full text-xs">
                                                    <XCircle size={10} /> Échec
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
